import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import {
  PERIOD_PRESETS,
  PERIOD_TODAY,
  periodLabel,
  type PeriodKey,
  type PeriodValue,
  type PeriodVariant,
} from '../../lib/period';

/**
 * The shared period selector (spec 41, board DR1PeriodSelector). Top-right of a
 * report header, the first header button. Closed: calendar · bold range name ·
 * grey dates · chevron. Open: the preset list for the variant, a divider, then
 * Custom range… which reveals From / To. Controlled — the page owns the value and
 * keeps it in the URL.
 */

export interface PeriodSelectorProps {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  /** `agent` offers 24h…60d; `report` offers 7d…Since it started. */
  variant: PeriodVariant;
  /** ISO yyyy-mm-dd the agent/recall/campaign started — the custom-range floor. */
  startedISO: string;
  /** "3 Mar 2026" — shown in the custom-range note. */
  startedLabel?: string;
  className?: string;
}

const MENU_GAP = 8;
const MENU_WIDTH = 290;

function toISO(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function PeriodSelector({ value, onChange, variant, startedISO, startedLabel, className }: PeriodSelectorProps) {
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(value.key === 'custom');
  const [draftFrom, setDraftFrom] = useState(value.from ?? startedISO);
  const [draftTo, setDraftTo] = useState(value.to ?? toISO(PERIOD_TODAY));

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const left = Math.min(Math.max(r.right - MENU_WIDTH, MENU_GAP), window.innerWidth - MENU_WIDTH - MENU_GAP);
    setPos({ left, top: r.bottom + MENU_GAP });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    setShowCustom(value.key === 'custom');
    place();
  }, [open, place, value.key]);

  useEffect(() => {
    if (!open) return;
    const on = () => {
      place();
    };
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const { name, dates } = periodLabel(value, startedISO);
  const presets = PERIOD_PRESETS[variant];
  const todayISO = toISO(PERIOD_TODAY);

  const pick = (key: PeriodKey) => {
    if (key === 'custom') {
      setShowCustom(true);
      return;
    }
    onChange({ key });
    setOpen(false);
  };

  const applyCustom = () => {
    onChange({ key: 'custom', from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const row = (key: PeriodKey, label: string) => {
    const active = key === 'custom' ? showCustom || value.key === 'custom' : value.key === key;
    return (
      <button
        key={key}
        type="button"
        role="menuitemradio"
        aria-checked={active}
        onClick={() => {
          pick(key);
        }}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] transition-colors',
          active
            ? 'bg-[var(--brand-indigo-tint)] font-bold text-[var(--brand-indigo)]'
            : 'font-medium text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]',
        )}
      >
        <span className="flex-1">{label}</span>
        {active ? <Icon kind="check" size={14} stroke={1.8} color="var(--brand-indigo)" /> : null}
      </button>
    );
  };

  const menu =
    open && pos
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{ position: 'fixed', left: pos.left, top: pos.top, width: MENU_WIDTH, zIndex: 'var(--z-dropdown)' }}
            className="rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)] p-2 shadow-[0_14px_36px_rgba(20,24,40,0.18)]"
          >
            {presets.map((p) => (p.key === 'custom' ? null : row(p.key, p.label)))}
            <div className="my-1.5 h-px bg-[var(--border-default)]" />
            {row('custom', 'Custom range…')}
            {showCustom ? (
              <div className="mt-1.5 border-t border-[var(--border-default)] px-3 pb-1 pt-2.5">
                <div className="flex gap-2.5">
                  <label className="flex-1">
                    <span className="mb-1 block text-[11px] font-bold text-[var(--text-muted)]">From</span>
                    <input
                      type="date"
                      value={draftFrom}
                      min={startedISO}
                      max={todayISO}
                      onChange={(e) => {
                        setDraftFrom(e.target.value);
                      }}
                      className="w-full rounded-lg border border-[var(--border-default)] px-2.5 py-[7px] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                    />
                  </label>
                  <label className="flex-1">
                    <span className="mb-1 block text-[11px] font-bold text-[var(--text-muted)]">To</span>
                    <input
                      type="date"
                      value={draftTo}
                      min={startedISO}
                      max={todayISO}
                      onChange={(e) => {
                        setDraftTo(e.target.value);
                      }}
                      className="w-full rounded-lg border border-[var(--border-default)] px-2.5 py-[7px] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                    />
                  </label>
                </div>
                <div className="mt-2 text-[11.5px] text-[var(--text-muted)]">
                  From {startedLabel ?? 'when it started'} (when it started) to today.
                </div>
                <div className="mt-2.5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustom(false);
                      setOpen(false);
                    }}
                    className="rounded-[11px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-primary)]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyCustom}
                    className="rounded-[11px] border border-[var(--brand-indigo)] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
                  >
                    Apply
                  </button>
                </div>
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Change the period"
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 transition-colors hover:border-[#ccd4e4]"
      >
        <Icon kind="calendar" size={15} stroke={1.7} color="var(--text-secondary)" />
        {name ? <span className="text-[13px] font-bold text-[var(--text-primary)]">{name}</span> : null}
        <span className="text-[12px] text-[var(--text-muted)]">{dates}</span>
        <Icon kind="down" size={14} stroke={1.7} color="var(--text-secondary)" />
      </button>
      {menu}
    </div>
  );
}
